import React from "react";
import { Link, NavLink } from "react-router-dom";
import { ActionIcon, Burger, Menu } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { motion } from "framer-motion";
import { imgNbaLogo } from "../../assets";

export default function Navbar() {
  const [opened, { toggle, close, open }] = useDisclosure(false);

  const links: Array<{ to: string; label: string; end?: boolean }> = [
    { to: "/", label: "Home", end: true },
    { to: "/about", label: "About Us" },
    { to: "/contact", label: "Contact Us" },
    { to: "/blog", label: "Blog" },
  ];

  return (
    <motion.header
      className="site-header relative z-10"
      data-node-id="240:851"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
    >
      <motion.div
        className="container site-header__inner"
        initial="hidden"
        animate="show"
        variants={{
          hidden: { opacity: 0 },
          show: {
            opacity: 1,
            transition: { staggerChildren: 0.06, delayChildren: 0.05 },
          },
        }}
      >
        <motion.div
          className="brand flex items-center gap-3 min-w-0"
          data-node-id="240:852"
          variants={{
            hidden: { opacity: 0, y: -6 },
            show: { opacity: 1, y: 0 },
          }}
          whileHover={{ y: -1 }}
          transition={{ type: "spring", stiffness: 380, damping: 26 }}
        >
          <img
            className="brand__logo"
            src={imgNbaLogo}
            alt="NBA logo"
            data-node-id="240:853"
          />
          <div className="brand__text min-w-0" data-node-id="240:854">
            <div className="brand__name" data-node-id="240:855">
              NIGERIAN BAR ASSOCIATION
            </div>
            <div className="brand__sub" data-node-id="240:856">
              REMUNERATION PORTAL
            </div>
          </div>
        </motion.div>

        <motion.nav
          className="nav site-header__nav"
          aria-label="Primary"
          data-node-id="240:857"
          variants={{
            hidden: { opacity: 0, y: -6 },
            show: { opacity: 1, y: 0 },
          }}
        >
          {links.map((l) => (
            <motion.div
              key={l.to}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 520, damping: 30 }}
            >
              <NavLink
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  isActive
                    ? "nav__link nav__link--active transition-transform duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nba-green)] focus-visible:ring-offset-2"
                    : "nav__link transition-transform duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nba-green)] focus-visible:ring-offset-2"
                }
              >
                {l.label}
              </NavLink>
            </motion.div>
          ))}
        </motion.nav>

        <motion.div
          className="auth site-header__auth"
          data-node-id="240:865"
          variants={{
            hidden: { opacity: 0, y: -6 },
            show: { opacity: 1, y: 0 },
          }}
        >
          <motion.div
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 520, damping: 30 }}
          >
            <Link
              to="/login"
              data-node-id="240:866"
              className="transition-transform duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nba-green)] focus-visible:ring-offset-2"
            >
              Sign in
            </Link>
          </motion.div>
          <motion.div
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 520, damping: 30 }}
          >
            <Link
              className="button button--primary transition-transform duration-200 active:translate-y-0"
              to="/signup"
              data-node-id="240:867"
            >
              Sign Up
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          className="site-header__mobile"
          data-node-id="240:865"
          variants={{
            hidden: { opacity: 0, y: -6 },
            show: { opacity: 1, y: 0 },
          }}
        >
          <Menu
            opened={opened}
            onChange={(nextOpened) => (nextOpened ? open() : close())}
            position="bottom-end"
            withinPortal={false}
            shadow="md"
            width={220}
          >
            <Menu.Target>
              <motion.div
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
              >
                <ActionIcon
                  variant="subtle"
                  color="dark"
                  aria-label="Open navigation menu"
                >
                  <Burger opened={opened} size="sm" />
                </ActionIcon>
              </motion.div>
            </Menu.Target>
            <Menu.Dropdown>
              {links.map((l) => (
                <Menu.Item
                  key={l.to}
                  component={Link}
                  to={l.to}
                  onClick={close}
                >
                  {l.label}
                </Menu.Item>
              ))}
              <Menu.Divider />
              <Menu.Item component={Link} to="/login" onClick={close}>
                Sign in
              </Menu.Item>
              <Menu.Item component={Link} to="/signup" onClick={close}>
                Sign Up
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </motion.div>
      </motion.div>
    </motion.header>
  );
}
